import { startAutomaticListsSyncPolling } from '../automatic-lists-sync/sync-status-poller.js';

const SYNC_SECTION_SWAP_MS = 200;

/**
 * @param {{
 *   page: HTMLElement,
 *   searchInput: HTMLInputElement,
 *   reinitializeSections: (animateCards: boolean) => void,
 *   isSearchActive: () => boolean,
 *   getSearchRevision: () => number,
 *   getListSortQuery: () => {automatic: string, manual: string}
 * }} options
 */
export function initAutomaticListsSyncFlow(options) {
    const {
        page,
        searchInput,
        reinitializeSections,
        isSearchActive,
        getSearchRevision,
        getListSortQuery,
    } = options;

    if (!(page instanceof HTMLElement) || !(searchInput instanceof HTMLInputElement)) {
        return;
    }

    if (
        typeof reinitializeSections !== 'function'
        || typeof isSearchActive !== 'function'
        || typeof getSearchRevision !== 'function'
        || typeof getListSortQuery !== 'function'
    ) {
        return;
    }

    const automaticSection = resolveAutomaticSection(page);
    if (!(automaticSection instanceof HTMLElement) || automaticSection.dataset.syncPending !== '1') {
        return;
    }

    const syncStatusUrl = normalizeUrl(automaticSection.dataset.syncStatusUrl);
    const automaticSectionUrl = normalizeUrl(automaticSection.dataset.automaticSectionUrl);

    if (syncStatusUrl === '' || automaticSectionUrl === '') {
        return;
    }

    startAutomaticListsSyncPolling({
        statusElement: page.querySelector('[data-lists-sync-status]'),
        syncStatusUrl,
        refresh: refreshAutomaticSection,
    });

    async function refreshAutomaticSection() {
        const sectionElement = resolveAutomaticSection(page);
        if (!(sectionElement instanceof HTMLElement)) {
            return false;
        }

        if (isSearchActive() || sectionElement.dataset.loading === 'true') {
            return false;
        }

        const searchRevision = getSearchRevision();
        const currentQuery = normalizeQuery(searchInput.value);
        const currentAutomaticPage = resolveAutomaticPageFromUrl();
        const currentManualPage = resolveManualPageFromUrl();
        const currentSortQuery = getListSortQuery();

        sectionElement.dispatchEvent(new CustomEvent('pagination:invalidate', { bubbles: true }));

        try {
            const requestUrl = new URL(automaticSectionUrl, window.location.origin);
            if (currentQuery !== '') {
                requestUrl.searchParams.set('q', currentQuery);
            }
            if (currentAutomaticPage !== '') {
                requestUrl.searchParams.set('automatic_page', currentAutomaticPage);
            }
            if (currentManualPage !== '') {
                requestUrl.searchParams.set('manual_page', currentManualPage);
            }
            requestUrl.searchParams.set('automatic_sort', currentSortQuery.automatic);
            requestUrl.searchParams.set('manual_sort', currentSortQuery.manual);

            const response = await fetch(requestUrl.toString(), {
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });

            if (!response.ok) {
                throw new Error(`Automatic section refresh failed with status ${response.status}`);
            }

            const html = await response.text();
            const incomingSection = parseIncomingAutomaticSection(html);

            if (!(incomingSection instanceof HTMLElement)) {
                throw new Error('Automatic section refresh returned invalid markup.');
            }

            if (isRefreshStale(
                sectionElement,
                searchRevision,
                currentQuery,
                currentAutomaticPage,
                currentManualPage,
                currentSortQuery,
            )) {
                return false;
            }

            sectionElement.classList.add('lists-sync-refresh-leaving');
            await wait(SYNC_SECTION_SWAP_MS);
            if (isRefreshStale(
                sectionElement,
                searchRevision,
                currentQuery,
                currentAutomaticPage,
                currentManualPage,
                currentSortQuery,
            )) {
                sectionElement.classList.remove('lists-sync-refresh-leaving');

                return false;
            }
            sectionElement.replaceWith(incomingSection);

            incomingSection.classList.add('lists-sync-refresh-entering');
            requestAnimationFrame(() => {
                incomingSection.classList.add('lists-sync-refresh-entering-active');
            });
            window.setTimeout(() => {
                incomingSection.classList.remove('lists-sync-refresh-entering', 'lists-sync-refresh-entering-active');
            }, SYNC_SECTION_SWAP_MS);

            incomingSection.dataset.syncPending = '0';
            reinitializeSections(true);

            return true;
        } catch (_error) {
            return false;
        }
    }

    function isRefreshStale(
        sectionElement,
        searchRevision,
        query,
        automaticPage,
        manualPage,
        sortQuery,
    ) {
        return isSearchActive()
            || getSearchRevision() !== searchRevision
            || sectionElement.dataset.loading === 'true'
            || sectionElement !== resolveAutomaticSection(page)
            || normalizeQuery(searchInput.value) !== query
            || resolveAutomaticPageFromUrl() !== automaticPage
            || resolveManualPageFromUrl() !== manualPage
            || getListSortQuery().automatic !== sortQuery.automatic
            || getListSortQuery().manual !== sortQuery.manual;
    }

    return { refreshAutomaticSection };
}

function resolveAutomaticSection(page) {
    const section = page.querySelector('#automatic-lists-section');

    return section instanceof HTMLElement ? section : null;
}

function parseIncomingAutomaticSection(html) {
    const template = document.createElement('template');
    template.innerHTML = html.trim();

    const section = template.content.querySelector('#automatic-lists-section')
        ?? template.content.querySelector('[data-lists-section="automatic"]');

    return section instanceof HTMLElement ? section : null;
}

function normalizeUrl(value) {
    return String(value ?? '').trim();
}

function normalizeQuery(value) {
    return String(value).trim();
}

function resolveAutomaticPageFromUrl() {
    return new URL(window.location.href).searchParams.get('automatic_page') ?? '';
}

function resolveManualPageFromUrl() {
    return new URL(window.location.href).searchParams.get('manual_page') ?? '';
}

function wait(ms) {
    return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });
}
